/**
 * Reconciles the cart back to a snapshot after a checkout attempt.
 *
 * Two producers write the `birch:cart:pendingRestore` snapshot, with different
 * reconcile semantics selected by `snapshot.mode`:
 *
 *   'union'  (partial checkout — component-cart-items.js)
 *     The user checked out a SUBSET of the cart; the unselected items were
 *     snapshotted and removed so checkout only saw the selected ones. Here we
 *     only ADD BACK the snapshotted items the cart is missing — we must never
 *     remove anything, or we'd wipe the selected items if the user abandoned.
 *
 *   'reset'  (buy-now — accelerated-checkout.liquid)
 *     "Buy it now" is a BYPASS-THE-CART action, but Shopify's accelerated
 *     checkout builds itself from the cart and leaves the bought product in the
 *     cart. So after a buy-now the cart should return to EXACTLY its pre-buy-now
 *     state: re-add anything an order cleared AND remove the lingering buy-now
 *     item. The whole pre-buy-now cart is snapshotted, so we make the cart match
 *     it line-for-line.
 *
 * Both modes are content-aware and idempotent, so they behave the same whether
 * an order was placed, the user abandoned, or the cart token rotated. We
 * deliberately do NOT gate on cart-token change: the token also rotates when an
 * accelerated / Shop Pay checkout is merely *created*, so it is a false-positive
 * signal for "order placed" and previously duplicated the cart on buy-now
 * abandonment.
 *
 * The snapshot is cleared after one reconcile so we never restore twice, and
 * stale snapshots (>24h) are discarded to avoid resurrecting forgotten items.
 */

const STORAGE_KEY = 'birch:cart:pendingRestore';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Composite line identity — MUST match cart-sync.js #keyOf so "is this item
// already in the cart?" is exact across variant + selling plan + line
// properties (two lines differ only if one of these differs).
function keyOf(descriptor) {
  const id = String(descriptor.id);
  const plan = descriptor.selling_plan ? String(descriptor.selling_plan) : '';
  const props = descriptor.properties;
  let sig = '';
  if (props && typeof props === 'object') {
    const keys = Object.keys(props).sort();
    if (keys.length) sig = keys.map((k) => `${k}=${props[k]}`).join('&');
  }
  return `${id}::${plan}::${sig}`;
}

// /cart.js line → descriptor shape keyOf understands.
function keyOfCartLine(line) {
  return keyOf({
    id: line.variant_id,
    properties: line.properties && Object.keys(line.properties).length ? line.properties : undefined,
    selling_plan: line.selling_plan_allocation?.selling_plan?.id,
  });
}

async function restorePendingItems() {
  let raw;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch (_) {
    return;
  }
  if (!raw) return;

  let snapshot;
  try {
    snapshot = JSON.parse(raw);
  } catch (_) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }

  const { token: previousToken, items, savedAt, mode } = snapshot || {};
  // 'reset' resets the whole cart to the snapshot (buy-now); anything else is
  // the additive partial-checkout restore.
  const isReset = mode === 'reset';
  if (!Array.isArray(items)) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  // An additive restore with nothing to add is a no-op. A 'reset' with an empty
  // snapshot is meaningful — it empties the cart back to its pre-buy-now state.
  if (items.length === 0 && !isReset) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }

  if (typeof savedAt === 'number' && Date.now() - savedAt > MAX_AGE_MS) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }

  const cartUrl = (typeof Theme !== 'undefined' && Theme.routes?.cart_url ? Theme.routes.cart_url : '/cart') + '.js';
  const cartAddUrl =
    typeof Theme !== 'undefined' && Theme.routes?.cart_add_url ? Theme.routes.cart_add_url : '/cart/add.js';
  const cartUpdateUrl =
    typeof Theme !== 'undefined' && Theme.routes?.cart_update_url ? Theme.routes.cart_update_url : '/cart/update';

  let currentToken;
  /** @type {Map<string, any>} key → current top-level /cart.js line */
  let currentLineByKey = new Map();
  try {
    const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    currentToken = cart.token;
    for (const line of cart.items || []) {
      // Bundle CHILD lines are managed by their parent and can't be re-added /
      // removed directly; ignore them so they never match a snapshot entry.
      if (line.parent_relationship?.parent || !line.variant_id) continue;
      currentLineByKey.set(keyOfCartLine(line), line);
    }
  } catch (_) {
    return;
  }

  const orderPlaced = previousToken && currentToken && previousToken !== currentToken; // diagnostics only

  // Snapshot keyed for lookup (last write wins if a variant appears twice).
  const snapshotByKey = new Map();
  for (const item of items) snapshotByKey.set(keyOf(item), item);

  // ADD: snapshot lines missing from the cart (both modes).
  const additions = [];
  for (const [key, item] of snapshotByKey) {
    if (!currentLineByKey.has(key)) additions.push(item);
  }

  // REMOVE (reset only): cart lines that are NOT in the snapshot — i.e. the
  // lingering buy-now item — plus any line whose quantity drifted above the
  // snapshot. Set the absolute quantity by line key (0 removes).
  /** @type {Record<string, number>} line.key → absolute quantity */
  const updates = {};
  if (isReset) {
    for (const [key, line] of currentLineByKey) {
      const snapItem = snapshotByKey.get(key);
      const targetQty = snapItem ? snapItem.quantity : 0;
      if (targetQty !== line.quantity) updates[line.key] = targetQty;
    }
  }

  const hasUpdates = Object.keys(updates).length > 0;
  if (additions.length === 0 && !hasUpdates) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }

  try {
    // Removals/quantity-resets first so a re-add can't collide with a line
    // we're about to zero out.
    if (hasUpdates) {
      const response = await fetch(cartUpdateUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        console.warn('cart-restore: failed to reset cart quantities', response.status, text);
        return;
      }
    }

    if (additions.length) {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: additions }),
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        console.warn('cart-restore: failed to re-add snapshotted items', response.status, text);
        return;
      }
    }
  } catch (error) {
    console.warn('cart-restore: error reconciling cart', error);
    return;
  }

  localStorage.removeItem(STORAGE_KEY);

  // Refresh cart UI (cart-icon bubble, header actions, etc.) so the user
  // doesn't have to reload the page to see the reconciled cart.
  let updatedCart;
  try {
    const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
    if (response.ok) updatedCart = await response.json();
  } catch (_) {
    // Non-fatal; the bubble will sync on the next cart interaction.
  }

  if (updatedCart) {
    const cartUpdate = new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: updatedCart,
        sourceId: 'cart-restore',
        data: {
          // Not `item_count` — a bundle is several cart lines but one item in
          // the bubble. This file is a classic script and cannot import the
          // helper, so it reads the copy cart-count.js hangs off `Theme`.
          source: 'cart-restore',
          itemCount:
            typeof Theme !== 'undefined' && Theme.cartDisplayCount
              ? Theme.cartDisplayCount(updatedCart)
              : updatedCart.item_count,
        },
      },
    });
    document.dispatchEvent(cartUpdate);
  }

  document.dispatchEvent(
    new CustomEvent('cart-restore:complete', {
      detail: { orderPlaced, mode: isReset ? 'reset' : 'union', restoredCount: additions.length },
    })
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', restorePendingItems, { once: true });
} else {
  restorePendingItems();
}
